import { useId, useState, type FormEvent } from "react";
import { Link } from "wouter";
import { isPaymentRequired } from "../../api/errors";
import { useCreateInstance, useProviderCredentials } from "../../api/queries";
import { useAuth } from "../../auth/AuthContext";
import { canSeeBilling } from "../../auth/roleView";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { Modal } from "../../components/Modal";
import { useToast } from "../../toast/ToastContext";
import "../../components/Form.css";

interface ProvisionInstanceModalProps {
  open: boolean;
  fleetId: string;
  onClose: () => void;
}

// Hetzner's own region/server-type identifiers, passed through verbatim —
// the server does not validate these against a catalog (see
// CreateInstanceInput in api/client.ts), it forwards them straight to the
// Hetzner API and surfaces whatever error comes back. A curated shortlist
// rather than every Hetzner option, matching this flow's "on-ramp, not the
// full provider console" scope.
const REGIONS = [
  { value: "fsn1", label: "Falkenstein, DE (fsn1)" },
  { value: "nbg1", label: "Nuremberg, DE (nbg1)" },
  { value: "hel1", label: "Helsinki, FI (hel1)" },
  { value: "ash", label: "Ashburn, US (ash)" },
];

const SERVER_TYPES = [
  { value: "cx22", label: "cx22 — 2 vCPU, 4 GB RAM" },
  { value: "cx32", label: "cx32 — 4 vCPU, 8 GB RAM" },
  { value: "cx42", label: "cx42 — 8 vCPU, 16 GB RAM" },
];

export function ProvisionInstanceModal({ open, fleetId, onClose }: ProvisionInstanceModalProps) {
  const { session } = useAuth();
  const credentialFieldId = useId();
  const regionFieldId = useId();
  const serverTypeFieldId = useId();
  const { pushToast } = useToast();
  const mutation = useCreateInstance(fleetId);
  const { data: credentialsPage, isPending: credentialsPending } = useProviderCredentials(fleetId);

  const [providerCredId, setProviderCredId] = useState("");
  const [region, setRegion] = useState(REGIONS[0].value);
  const [serverType, setServerType] = useState(SERVER_TYPES[0].value);

  const credentials = credentialsPage?.items ?? [];
  // 402: refused because of a licensed limit, never because anything running
  // was touched (charter §1 safety corollary) — render the server's own
  // plain-language message rather than a generic failure (Q-7).
  const paymentRequired = mutation.isError && isPaymentRequired(mutation.error);
  const canViewBilling = canSeeBilling(session?.role);

  function handleClose() {
    setProviderCredId("");
    setRegion(REGIONS[0].value);
    setServerType(SERVER_TYPES[0].value);
    mutation.reset();
    onClose();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    mutation.mutate(
      { providerCredId, region, serverType },
      {
        onSuccess: () => {
          pushToast({ title: "Provisioning started…", tone: "success" });
          handleClose();
        },
      },
    );
  }

  const canSubmit = providerCredId.length > 0 && !mutation.isPending;

  return (
    <Modal open={open} title="Provision an instance" onClose={handleClose}>
      <form onSubmit={handleSubmit} noValidate>
        {mutation.isError && !paymentRequired && (
          <p className="form-error" role="alert">
            {mutation.error instanceof Error ? mutation.error.message : "Couldn't provision this instance."}
          </p>
        )}

        {paymentRequired && mutation.error && (
          <Banner tone="warning" title="Licensed capacity reached">
            {mutation.error.message}
            {canViewBilling && (
              <>
                {" "}
                <Link href="/billing">See your plan</Link>.
              </>
            )}
          </Banner>
        )}

        <label className="form-field" htmlFor={credentialFieldId}>
          <span>Provider credential</span>
          <select
            id={credentialFieldId}
            value={providerCredId}
            onChange={(e) => setProviderCredId(e.target.value)}
            required
            disabled={credentialsPending}
          >
            <option value="" disabled>
              {credentialsPending ? "Loading credentials…" : "Select a credential"}
            </option>
            {credentials.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
          {!credentialsPending && credentials.length === 0 && (
            <span className="form-field__hint">
              No provider credentials yet — add a Hetzner API token first.
            </span>
          )}
        </label>

        <label className="form-field" htmlFor={regionFieldId}>
          <span>Region</span>
          <select id={regionFieldId} value={region} onChange={(e) => setRegion(e.target.value)} required>
            {REGIONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </label>

        <label className="form-field" htmlFor={serverTypeFieldId}>
          <span>Server type</span>
          <select id={serverTypeFieldId} value={serverType} onChange={(e) => setServerType(e.target.value)} required>
            {SERVER_TYPES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          <span className="form-field__hint">Billed by Hetzner directly — Comebye never marks this up.</span>
        </label>

        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={!canSubmit}>
            {mutation.isPending ? "Provisioning…" : "Provision"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
